import { captureException } from '@sentry/react'
import { TAppDispatch, TGetState } from 'store/types'
import {
  IAIImageProject,
  IReferenceImage,
  IGenerateFormState
} from 'store/types/aiImage'
import { ApiError, ReduxThunkError } from 'utils/errors'
import { getAiImagesByIds_API } from 'api'
import { ErrorKeys } from 'consts/errorKeys'
import { cacheSelectors } from 'store/features/cache/selectors'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { PROJECT_MEDIA_TYPES } from 'consts'
import { filterValidImages } from 'utils/aiImage'
import { aiImageSelectors } from '../selectors'
import { withAppThunks } from 'store/with-app-thunks'
import { handleApiError } from 'store/utils'
import aiImageSlice from '../slice'
const { actions } = aiImageSlice

const getValidModelCode = (modelCode: string, getState: TGetState): string => {
  const state = getState()
  const modelsData = aiImageSelectors.modelsData(state)

  const model = modelsData.find(model => model.code === modelCode)

  if (model?.name) {
    return model.code
  }

  const defaultModel = aiImageSelectors.defaultModel(state)
  return defaultModel.code
}

const getAiImageByAssetId = (assetId: number) =>
  withAppThunks(
    appThunks => async (dispatch: TAppDispatch, getState: TGetState) => {
      if (isNaN(assetId)) {
        return
      }
      const state = getState()
      const aiVideoCache = cacheSelectors.aiImageCache(state)
      const existingItem = aiVideoCache[assetId]

      if (existingItem) {
        // Re-validate reference images even for cached data
        // in case they were deleted or became invalid
        if (existingItem.refImages?.length) {
          const validatedRefImages = await filterValidImages(
            existingItem.refImages
          )
          const updatedItem = {
            ...existingItem,
            refImages: validatedRefImages
          }
          // Update cache with validated ref images
          dispatch(appThunks.cacheThunks.addToAiImageCache([updatedItem]))
          return updatedItem
        }
        return existingItem
      }

      try {
        const { data } = await getAiImagesByIds_API({
          ids: [Number(assetId)]
        })

        const [rawProject] = data

        const project = rawProject?.refImages?.length
          ? {
              ...rawProject,
              refImages: await filterValidImages(rawProject.refImages)
            }
          : rawProject

        dispatch(appThunks.cacheThunks.addToAiImageCache([project]))
        return project
      } catch (error) {
        if (!(error instanceof ApiError)) {
          captureException(new ReduxThunkError(error))
        }

        try {
          const { data } = await getAiImagesByIds_API({
            ids: [Number(assetId)]
          })
          dispatch(appThunks.cacheThunks.addToAiImageCache(data))
          return data[0]
        } catch (error) {
          if (!(error instanceof ApiError)) {
            captureException(new ReduxThunkError(error))
          }
          dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
        }
      }
    }
  )

const setGenerationParamsByAssetId =
  (assetId: number) => async (dispatch: TAppDispatch, getState: TGetState) => {
    try {
      const data = (await dispatch(
        getAiImageByAssetId(assetId)
      )) as IAIImageProject
      if (data) {
        const validModelCode = getValidModelCode(data.model, getState)
        const refImages: IReferenceImage[] = data.refImages.map(
          (refImgUrl, index) => ({
            id: index,
            url: refImgUrl,
            thumbnailUrl: refImgUrl,
            width: 100,
            height: 100
          })
        )

        const params: IGenerateFormState = {
          id: data.id,
          refImages,
          modelCode: validModelCode,
          sourceModelCode: data.model,
          prompt: data.prompt,
          styleId: data.styleId,
          aspectRatio: data.aspectRatio,
          images: data.images,
          width: data.width,
          height: data.height,
          targetMediaItemId: null
        }
        dispatch(actions.setGenerationForm(params))
      }
    } catch (error) {
      captureException(new ReduxThunkError(error))
    }
  }

const setRegenerationForm =
  (mediaItemId: string) =>
  async (dispatch: TAppDispatch, getState: TGetState) => {
    dispatch(actions.setTargetMediaItemId(null))
    const state = getState()
    const mediaItems = projectDataSelectors.mediaItems(state)
    const targetMedia = mediaItems.find(
      item =>
        item.id === mediaItemId &&
        (item.type === PROJECT_MEDIA_TYPES.aiImage ||
          item.type === PROJECT_MEDIA_TYPES.image)
    )
    if (!targetMedia?.assetId) {
      return
    }
    try {
      const data = (await dispatch(
        getAiImageByAssetId(Number(targetMedia.assetId))
      )) as IAIImageProject
      if (data) {
        const validModelCode = getValidModelCode(data.model, getState)
        const refImages: IReferenceImage[] = data.refImages.map(
          (refImgUrl, index) => ({
            id: index,
            url: refImgUrl,
            thumbnailUrl: refImgUrl,
            width: 100,
            height: 100
          })
        )

        const params: IGenerateFormState = {
          id: data.id,
          refImages,
          modelCode: validModelCode,
          sourceModelCode: data.model,
          prompt: data.prompt,
          styleId: data.styleId,
          aspectRatio: data.aspectRatio,
          images: data.images,
          width: data.width,
          height: data.height,
          targetMediaItemId: mediaItemId
        }
        dispatch(actions.setGenerationForm(params))
      }
    } catch (error) {
      captureException(new ReduxThunkError(error))
    }
  }

export const formThunks = {
  getAiImageByAssetId,
  setGenerationParamsByAssetId,
  setRegenerationForm
}
